/**
 * Browser-storage settings — the single source of truth for everything
 * this app persists in localStorage.
 *
 * All keys live under the `nitter:` namespace so the Settings panel can
 * enumerate (and clear) exactly what this app has written. The /view
 * banner page (functions/_lib/banner.tsx) and its client script
 * (src/banner/main.ts) read the same keys with the same raw names — this
 * module is the single inventory of them.
 */

const NS = "nitter:";

export const Keys = {
  /** domain string — the user's preferred instance */
  preferred: `${NS}preferred-instance`,
  /** "1" | "0" — open /view links directly on x.com instead of Nitter */
  redirectX: `${NS}redirect-x`,
  /** string — custom uptime-JSON feed URL (empty = server default) */
  customSource: `${NS}custom-source-url`,
  /** JSON map { [tipKey]: ackedAt(ms) } — tooltips already acknowledged */
  tipsAcked: `${NS}tips-acked`,
  /** JSON map { [domain]: accumulated local bias delta } — the ONE personal
   *  record, shared by the dashboard and the /view banner (via
   *  src/lib/boost.ts). */
  myBias: `${NS}my-bias`,
  /** "top" | "bottom" — /view toolbar + pill dock position */
  bannerDock: `${NS}banner-dock`,
  /** JSON { x, y } | null — dragged position of the /view minimized pill */
  pillPos: `${NS}pill-pos`,
    /** @legacy Pre-unification banner boost store (`nitter:bias`). boost.ts
     *  now shares `my-bias`; migrateLegacyBias() merges any leftover data
     *  into it and removes this key. Never written anymore. */
    bias: `${NS}bias`,
  /** "1" — the visitor clicked a Donate CTA. Records INTENT, not a
   *  completed donation (there is no PayPal return callback here). */
  donateClicked: `${NS}donate-clicked`,
  /** ms epoch string — when the Donate CTA was clicked; drives the
   *  DONATE_BACKOFF_MS window after which the pledge quietly returns. */
  donateClickedAt: `${NS}donate-clicked-at`,
  /** JSON { target, raised, deadline } — optional per-browser override of
   *  the funding-drive numbers (src/lib/donate.ts), so a drive can be
   *  pinned manually without a redeploy. Absent = derived curve. */
  donateDrive: `${NS}donate-drive`,
} as const;

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage disabled / private mode
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function getPreferredInstance(): string | null {
  return readRaw(Keys.preferred);
}

export function setPreferredInstance(domain: string | null): void {
  writeRaw(Keys.preferred, domain);
}

export function getRedirectX(): boolean {
  return readRaw(Keys.redirectX) === "1";
}

export function setRedirectX(on: boolean): void {
  writeRaw(Keys.redirectX, on ? "1" : "0");
}

// Custom data source URL
export function getCustomSource(): string {
  return readRaw(Keys.customSource) ?? "";
}

export function setCustomSource(url: string): void {
  const trimmed = url.trim();
  if (trimmed) writeRaw(Keys.customSource, trimmed);
  else writeRaw(Keys.customSource, null);
}

// --- Local bias record ----------------------------------------------------
// One personal record shared by the dashboard AND the /view banner (via
// src/lib/boost.ts). boost.ts used to keep banner nudges in a separate
// `nitter:bias` key; leftover data is merged in once, then the legacy key
// is dropped.

/** One-time migration of the legacy per-banner `nitter:bias` map. */
function migrateLegacyBias(): void {
  const raw = readRaw(Keys.bias);
  if (raw == null) return;
  // Drop the legacy key first (corrupt or not), so this runs at most once.
  writeRaw(Keys.bias, null);
  try {
    const legacy = JSON.parse(raw);
    if (!legacy || typeof legacy !== "object") return;
    const map = getMyBias(); // safe: the legacy key is already gone
    for (const [domain, v] of Object.entries(legacy)) {
      // noinspection SuspiciousTypeOfGuard
      if (typeof v !== "number" || !isFinite(v)) continue;
      const next = Math.round(((map[domain] ?? 0) + v) * 10) / 10;
      if (next === 0) delete map[domain];
      else map[domain] = next;
    }
    writeRaw(
      Keys.myBias,
      Object.keys(map).length ? JSON.stringify(map) : null,
    );
  } catch {
    /* corrupt legacy data — already dropped above */
  }
}

export type MyBiasMap = Record<string, number>;

export function getMyBias(): MyBiasMap {
  migrateLegacyBias();
  const raw = readRaw(Keys.myBias);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as MyBiasMap)
      : {};
  } catch {
    return {};
  }
}

/** Add `delta` to the local record for `domain` and return the new total. */
export function addMyBias(domain: string, delta: number): number {
  const map = getMyBias();
  const total = Math.round(((map[domain] ?? 0) + delta) * 10) / 10;
  if (total === 0) delete map[domain];
  else map[domain] = total;
  writeRaw(
    Keys.myBias,
    Object.keys(map).length ? JSON.stringify(map) : null,
  );
  return total;
}

/** Remove ONE domain from the local bias record (Settings → per-domain
 *  reset). Returns the previous tally, or null when the domain had none. */
export function removeMyBias(domain: string): number | null {
  const map = getMyBias();
  const prev = map[domain];
  if (prev == null) return null;
  delete map[domain];
  writeRaw(
    Keys.myBias,
    Object.keys(map).length ? JSON.stringify(map) : null,
  );
  return prev;
}

/** Undo for removeMyBias: put a previous tally back, but only when the
 *  domain has no newer entry (the user may have re-boosted meanwhile). */
export function restoreMyBias(domain: string, prev: number): boolean {
  if (!domain) return false;
  const map = getMyBias();
  if (map[domain] != null) return false;
  map[domain] = prev;
  writeRaw(Keys.myBias, JSON.stringify(map));
  return true;
}

/** Clear the whole local bias record (Settings → “Reset all”). */
export function clearMyBias(): void {
  writeRaw(Keys.myBias, null);
}

// --- Donation click tracking ----------------------------------------------
// Clicking a Donate CTA (dashboard card or the /view chip) records the
// click — an intent signal, NOT a completed donation: there is no PayPal
// return callback in this project to confirm completion. The donation UI
// therefore only BACKS OFF for DONATE_BACKOFF_MS after a click, then
// quietly returns in case the donation never went through. Session-scoped
// dismissal (the × button) stays deliberately unpersisted, like the boost
// card's original design.

/** How long the donation UI stays hidden after a Donate click. */
export const DONATE_BACKOFF_MS = 14 * 24 * 60 * 60 * 1000; // two weeks

export function getDonateClicked(): boolean {
  return readRaw(Keys.donateClicked) === "1";
}

export function getDonateClickedAt(): number | null {
  const raw = readRaw(Keys.donateClickedAt);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Record a Donate CTA click. Starts (or restarts, if the previous
 *  backoff window has already lapsed) the backoff window. */
export function markDonateClicked(): void {
  const at = getDonateClickedAt();
  if (getDonateClicked() && at != null && Date.now() - at < DONATE_BACKOFF_MS)
    return; // click already recorded inside an active window
  writeRaw(Keys.donateClicked, "1");
  writeRaw(Keys.donateClickedAt, String(Date.now()));
}

/** True while the donation UI should stay hidden after a click. */
export function isDonateBackoff(): boolean {
  const at = getDonateClickedAt();
  return getDonateClicked() && at != null && Date.now() - at < DONATE_BACKOFF_MS;
}

// --- Tooltip acknowledgements ---------------------------------------------

export type TipsAckedMap = Record<string, number>;

export function getTipsAcked(): TipsAckedMap {
  const raw = readRaw(Keys.tipsAcked);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as TipsAckedMap)
      : {};
  } catch {
    return {};
  }
}

export function isTipAcked(key: string): boolean {
  return getTipsAcked()[key] != null;
}

/** Record a tooltip as acknowledged (idempotent). */
export function ackTip(key: string): void {
  if (!key) return;
  const map = getTipsAcked();
  if (map[key] != null) return;
  map[key] = Date.now();
  writeRaw(Keys.tipsAcked, JSON.stringify(map));
}

/** Undo for unackTip: re-acknowledge a tooltip with its ORIGINAL timestamp
 *  so the settings list keeps its history. No-op when the tip was
 *  acknowledged again in the meantime (never clobber newer state). */
export function reackTip(key: string, at: number): boolean {
  if (!key || !Number.isFinite(at) || at <= 0) return false;
  const map = getTipsAcked();
  if (map[key] != null) return false;
  map[key] = at;
  writeRaw(Keys.tipsAcked, JSON.stringify(map));
  return true;
}

/** Un-acknowledge a single tooltip: it will show again on the next hover
 *  (the tip engines read the map live, so no reload is needed). */
export function unackTip(key: string): void {
  if (!key) return;
  const map = getTipsAcked();
  if (map[key] == null) return;
  delete map[key];
  writeRaw(Keys.tipsAcked, Object.keys(map).length ? JSON.stringify(map) : null);
}

export function resetTipsAcked(): void {
  writeRaw(Keys.tipsAcked, null);
}

// --- /view toolbar dock + pill position ------------------------------------
// Read/written by the banner client (src/banner/main.ts), exposed here so
// this module stays the single inventory of `nitter:*` keys.

export type BannerDock = "top" | "bottom";

export function getBannerDock(): BannerDock {
  return readRaw(Keys.bannerDock) === "bottom" ? "bottom" : "top";
}

export function setBannerDock(pos: BannerDock): void {
  writeRaw(Keys.bannerDock, pos);
}

export type PillPos = { x: number; y: number; flip?: boolean };

export function getPillPos(): PillPos | null {
  const raw = readRaw(Keys.pillPos);
  if (!raw) return null;
  try {
    const p = JSON.parse(raw);
    if (
      p &&
      typeof p === "object" &&
      typeof (p as PillPos).x === "number" &&
      typeof (p as PillPos).y === "number"
    ) {
      return { x: (p as PillPos).x, y: (p as PillPos).y };
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function setPillPos(pos: PillPos | null): void {
  writeRaw(Keys.pillPos, pos ? JSON.stringify(pos) : null);
}

// --- Settings panel introspection ------------------------------------------

export type StorageRow = {
  key: string;
  label: string;
  value: string;
  /** Hidden rows are managed by the app itself (currently: the donation
   *  pledge state). They never appear in the Settings panel and cannot be
   *  reset individually — the user must not be able to control them. */
  hidden?: boolean;
};

/** Keys the user cannot control from the Settings UI. “Clear browser
 *  storage” also leaves these alone (see clearAllStorage) so the donation
 *  backoff / pledge state cannot be reset from the client. */
export const HIDDEN_KEYS: ReadonlySet<string> = new Set<string>([
  Keys.donateClicked,
  Keys.donateClickedAt,
  Keys.donateDrive,
]);

/** Remove one `nitter:*` setting (Settings panel per-row reset button).
 *  Keys outside this app's namespace and hidden (donation) keys are
 *  refused. Returns true when something was removed. */
export function removeSetting(key: string): boolean {
  if (!key.startsWith(NS) || HIDDEN_KEYS.has(key)) return false;
  if (readRaw(key) == null) return false;
  writeRaw(key, null);
  return true;
}

// --- Undo support -----------------------------------------------------------
// Destructive settings actions snapshot what they are about to destroy and
// offer an Undo button on their toast. Restores are deliberately timid:
// they only write back values that are CURRENTLY ABSENT, so an undo can
// never clobber a newer choice the user made after the destructive action.

/** Raw read of one `nitter:*` value — for undo snapshots taken immediately
 *  before a destructive action. Namespace-guarded; hidden keys read fine
 *  but restoreSetting refuses to write them back. */
export function peekSetting(key: string): string | null {
  if (!key.startsWith(NS)) return null;
  return readRaw(key);
}

/** Undo for removeSetting / resetTipsAcked / clearMyBias: write a snapshotted
 *  raw value back. Namespace + hidden guards apply, and the restore only
 *  happens when the key is currently empty (nothing newer was written). */
export function restoreSetting(key: string, value: string): boolean {
  if (!key.startsWith(NS) || HIDDEN_KEYS.has(key)) return false;
  if (readRaw(key) != null) return false;
  writeRaw(key, value);
  return true;
}

/** Snapshot every visible `nitter:*` key → value, taken right before
 *  “Clear browser storage” so it can be undone. Hidden (donation) keys are
 *  not included — they are never cleared in the first place. */
export function snapshotStorage(): Record<string, string> {
  const snap: Record<string, string> = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(NS) || HIDDEN_KEYS.has(k)) continue;
      const v = window.localStorage.getItem(k);
      if (v != null) snap[k] = v;
    }
  } catch {
    /* storage disabled — nothing to snapshot */
  }
  return snap;
}

/** Undo for clearAllStorage: write a snapshotStorage() snapshot back.
 *  Keys the user re-created in the meantime are left untouched.
 *  Returns how many keys were restored. */
export function restoreStorageSnapshot(snap: Record<string, string>): number {
  let restored = 0;
  for (const [k, v] of Object.entries(snap)) {
    if (restoreSetting(k, v)) restored++;
  }
  return restored;
}

/**
 * Everything this app has written to localStorage, for the Settings panel.
 * Hidden rows (donation state) are only included when explicitly asked
 * for — the regular panel never sees them.
 */
export function dumpStorage(includeHidden = false): StorageRow[] {
  const rows: StorageRow[] = [];
  const pref = getPreferredInstance();
  if (pref)
    rows.push({ key: Keys.preferred, label: "Preferred instance", value: pref });
  if (readRaw(Keys.redirectX))
    rows.push({ key: Keys.redirectX, label: "Redirect URLs to X.com", value: getRedirectX() ? "on" : "off" });

  const src = getCustomSource();
  if (src)
    rows.push({
      key: Keys.customSource,
      label: "Custom data source URL",
      value: src,
    });

  const myBias = getMyBias();
  const biasEntries = Object.entries(myBias);
  if (biasEntries.length)
    rows.push({
      key: Keys.myBias,
      label: "Your bias adjustments",
      value: biasEntries.map(([d, v]) => `${d}: ${v > 0 ? "+" : ""}${v}`).join(", "),
    });

  const tips = getTipsAcked();
  const tipCount = Object.keys(tips).length;
  if (tipCount)
    rows.push({
      key: Keys.tipsAcked,
      label: "Acknowledged tooltips",
      value: `${tipCount} tip${tipCount === 1 ? "" : "s"} hidden`,
    });

  // Donation pledge state — recorded for the app, but deliberately hidden
  // from the Settings panel: the user must not be able to control it.
  if (includeHidden && getDonateClicked()) {
    const at = getDonateClickedAt();
    rows.push({
      key: Keys.donateClicked,
      label: "Donation link clicked",
      value: "yes",
      hidden: true,
    });
    if (at != null)
      rows.push({
        key: Keys.donateClickedAt,
        label: "Donation click time",
        value: `${new Date(at).toLocaleString()}${
          isDonateBackoff() ? " (pledge hidden" : " (pledge shown"
        } until ${at != null ? new Date(at + DONATE_BACKOFF_MS).toLocaleDateString() : ""})`,
        hidden: true,
      });
  }

  return rows;
}

/** Remove every user-controllable `nitter:*` key (Settings → clear browser
 *  storage). Hidden keys (donation pledge state) are deliberately kept:
 *  the donation backoff must not be resettable from the client. */
export function clearAllStorage(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(NS) && !HIDDEN_KEYS.has(k)) doomed.push(k);
    }
    for (const k of doomed) window.localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}
